import { getGuideSlug } from "./guideSlugMap";
import { buildLocalizedPath } from "./detectLanguage";

const baseLang = (lang: string | undefined) =>
  (lang || "en").split("-")[0].toLowerCase();

export const legacyGuideCanonicalPaths: Record<string, Partial<Record<string, string>>> = {
  bankAccount: {
    en: "/open-bank-account-thailand-elite",
    fr: "/ouverture-de-compte-bancaire",
    de: "/open-bank-account-thailand-elite",
    it: "/open-bank-account-thailand-elite",
    ru: "/open-bank-account-thailand-elite",
    zh: "/open-bank-account-thailand-elite",
    ja: "/open-bank-account-thailand-elite",
  },
  // tm30 entry removed — canonical is now /guides/tm30 in every language.
  // The default getGuideCanonicalPath() builds /guides/<slug> when there's no
  // legacy override here, so internal links and breadcrumbs auto-point at the
  // modern URL. Old /what-is-tm30-all-about and /declaration-tm30-thailande
  // still serve via the worker's 301 redirect for inbound backlinks.
  residenceCert: {
    en: "/what-is-a-residence-certificate-and-how-to-get-it",
    fr: "/certificat-de-residence-thailande",
    de: "/what-is-a-residence-certificate-and-how-to-get-it",
    it: "/what-is-a-residence-certificate-and-how-to-get-it",
    ru: "/what-is-a-residence-certificate-and-how-to-get-it",
    zh: "/what-is-a-residence-certificate-and-how-to-get-it",
    ja: "/what-is-a-residence-certificate-and-how-to-get-it",
  },
  tdac: {
    en: "/blog/tdac-thailand-digital-arrival-card",
    fr: "/guides/tdac",
  },
};

export const legacyGuidePathToKey = Object.entries(legacyGuideCanonicalPaths).reduce<
  Record<string, string>
>((acc, [guideKey, paths]) => {
  for (const path of Object.values(paths)) {
    if (path) acc[path.toLowerCase()] = guideKey;
  }
  return acc;
}, {});

export function getGuideCanonicalPath(guideKey: string, lang: string | undefined): string {
  const langCode = baseLang(lang);
  const legacyPath =
    legacyGuideCanonicalPaths[guideKey]?.[langCode] ||
    legacyGuideCanonicalPaths[guideKey]?.en;

  return legacyPath || `/guides/${getGuideSlug(guideKey, langCode)}`;
}

/**
 * Build the final, ready-to-use href for a guide link in the current language.
 *
 * Unlike wrapping getGuideCanonicalPath() in <LocalizedLink>, this returns a
 * path that is already localized, so render it with a plain <Link>, NOT
 * <LocalizedLink>. This matters for legacy guide URLs and CMS-backed guide
 * entries like TDAC, where the canonical path may already be language-specific.
 */
export function getGuideHref(guideKey: string, lang: string | undefined): string {
  const langCode = baseLang(lang);
  return buildLocalizedPath(getGuideCanonicalPath(guideKey, langCode), langCode);
}

export function getApplicationCanonicalPath(lang: string | undefined): string {
  return baseLang(lang) === "fr" ? "/devenir-membre" : "/member-application-process";
}
